import * as DateFns from "date-fns";
import currency from "currency.js";
import {
  IDetailsLoan,
  IDetailsLoanSecurityMappings,
  IDetailsSecurity,
  IPdfReport,
  IResReport,
} from "../models/report";
import { expenseTypeData } from "../../../expenses";
import { decimalToPercentage } from "../../../util";
import { propertyTypeOptionsData, usageTypeData } from "../../../security";
import {
  incomeTypeOtherData,
  incomeTypeSalaryWagesData,
} from "../../../income";
import {
  LiabilityTypeCreditCardsData,
  LiabilityTypeLiabilitiesData,
} from "../../../liabilities";

const boarderType = [
  {
    label: "All Inclusive",
    value: "AllInclusive",
  },
  {
    label: "Room Charge Only",
    value: "RoomChargeOnly",
  },
];

export const reportBuilderUI = (report: any): IPdfReport => {
  return {
    reportId: report.reportId,
    currentLoanAmount: report?.currentLoanAmount,
    lenders: report?.lenders,
    includeWarningsErrors: report?.includeWarningsErrors,
    notes: report?.notes,
    reqReportData: requestBuilderUI(report?.reqReportData),
    resReportData: { report: responseBuilderUI(report?.resReportData?.Report) },
  };
};

export const requestBuilderUI = (data: any) => {
  return {
    details: {
      loanSecurityMapping: data?.Details?.LoanSecurityMapping?.map(
        (_i: any) => ({
          loanId: _i.LoanId,
          securityId: _i.SecurityId,
        }),
      ),
      security: data?.Details?.Security?.map((_i: any) => ({
        securityId: _i?.SecurityId,
        value: _i?.Value,
        isRBNZExempt: _i?.IsRBNZExempt,
        location: _i?.Location,
        securityType: _i?.SecurityType,
        usageType: _i?.UsageType,
        propertyType: _i?.PropertyType,
      })),
      loan: data?.Details?.Loan?.map((_i: any) => ({
        loanId: _i?.LoanId,
        loanType: _i?.LoanType,
        lender: _i?.Lender,
        lendingAction: _i?.LendingAction,
        liabilityType: _i?.LiabilityType,
        loanAmount: _i?.LoanAmount,
        topUpAmount: _i?.TopUpAmount,
        totalTermMonths: _i?.TotalTermMonths,
        interestOnlyTermMonths: _i?.InterestOnlyTermMonths,
        liability: {
          liabilityType: _i?.Liability?.LiabilityType,
          repaymentAmount: _i?.Liability?.RepaymentAmount,
          repaymentFrequency: _i?.Liability?.RepaymentFrequency,
          limit: _i?.Liability?.Limit,
          balance: _i?.Liability?.Balance,
          interestRate: _i?.Liability?.InterestRate,
          repaymentType: _i?.Liability?.RepaymentType,
          startDate: _i?.Liability?.StartDate,
          totalTermYears: _i?.Liability?.TotalTermYears,
          totalTermMonths: _i?.Liability?.TotalTermMonths,
          interestOnlyTermYears: _i?.Liability?.InterestOnlyTermYears,
          interestOnlyTermMonths: _i?.Liability?.InterestOnlyTermMonths,
          lender: _i?.Liability?.Lender,
          isRevolvingCredit: _i?.Liability?.IsRevolvingCredit,
        },
      })),
      applicantParty: data?.Details?.ApplicantParty?.map((_i: any) => ({
        applicant: _i?.Applicant?.map((d: any) => ({
          applicantId: d?.ApplicantId,
          name: d?.Name,
        })),
        numberOfDependents: _i?.NumberOfDependents,
        numberOfVehicles: _i?.NumberOfVehicles,
        expense: _i?.Expense?.map((d: any) => ({
          applicantId: d?.ApplicantId,
          expenseType: d?.ExpenseType,
          frequency: d?.Frequency,
          amount: d?.Amount,
          ...expenseTypeData?.reduce((_acc: any, _expense: any) => {
            if (_expense["ExpenseType.Code"] === d?.ExpenseType) {
              _acc = {
                group: _expense?.Group,
                label: _expense["ExpenseType.Name"],
              };
            }
            return _acc;
          }, {}),
        })),
        income: _i?.Income?.map((d: any) => ({
          applicantId: d?.ApplicantId,
          incomeType: d?.IncomeType,
          amountType: d?.AmountType,
          frequency: d?.Frequency,
          amount: d?.Amount,
          attributes: d?.Attributes,
        })),
        liability: _i?.Liability?.map((d: any) => ({
          applicantId: d?.ApplicantId,
          liabilityType: d?.LiabilityType,
          repaymentAmount: d?.RepaymentAmount,
          repaymentFrequency: d?.RepaymentFrequency,
          limit: d?.Limit,
          balance: d?.Balance,
          interestRate: d?.InterestRate,
          repaymentType: d?.RepaymentType,
          startDate: d?.StartDate,
          totalTermMonths: d?.TotalTermMonths,
          interestOnlyTermMonths: d?.InterestOnlyTermMonths,
          lender: d?.Lender,
          isRevolvingCredit: d?.IsRevolvingCredit,
        })),
      })),
    },
    lenders: data?.Lenders,
  };
};

export const responseBuilderUI = (data: any) => {
  return {
    id: data?.Id,
    cases: data?.Cases?.map((_case: any) => ({
      id: _case?.Id,
      loanAmount: _case?.LoanAmount,
      lenderResult: _case?.LenderResult?.map((_d: any) => ({
        lender: {
          id: _d?.Lender?.Id,
          key: _d?.Lender?.Key,
          name: _d?.Lender?.Name,
          logo: _d?.Lender?.Logo,
        },
        result: {
          assessedIncome: _d?.Result?.AssessedIncome?.map((_i: any) => ({
            group: _i?.Group,
            label: _i?.Label,
            entered: {
              amount: _i?.Entered?.Amount,
              frequency: _i?.Entered?.Frequency,
            },
            assessed: {
              amount: _i?.Assessed?.Amount,
              frequency: _i?.Assessed?.Frequency,
            },
          })),
          assessedExpense: _d?.Result?.AssessedExpense?.map((_i: any) => ({
            group: _i?.Group,
            label: _i?.Label,
            entered: {
              amount: _i?.Entered?.Amount,
              frequency: _i?.Assessed?.Frequency,
              interestRate: _i?.Entered?.InterestRate,
              definition: _i?.Entered?.Definition,
            },
            assessed: {
              amount: _i?.Assessed?.Amount,
              frequency: _i?.Assessed?.Frequency,
            },
          })),
          totalMonthlyIncome: _d?.Result?.TotalMonthlyIncome,
          totalMonthlyExpense: _d?.Result?.TotalMonthlyExpense,
          UMI: {
            value: _d?.Result?.UMI?.Value,
            required: _d?.Result?.UMI?.Required,
            status: _d?.Result?.UMI?.Status,
            securityUsageType: _d?.Result?.UMI?.SecurityUsageType,
            lvrValue: _d?.Result?.UMI?.LvrValue,
          },
          NSR: {
            value: _d?.Result?.NSR?.Value,
            required: _d?.Result?.NSR?.Required,
            status: _d?.Result?.NSR?.Status,
          },
          servicing: {
            type: _d?.Result?.Servicing?.Type,
            value: _d?.Result?.Servicing?.Value,
            required: _d?.Result?.Servicing?.Required,
            status: _d?.Result?.Servicing?.Status,
          },
          LVR: _d?.Result?.LVR?.map((_i: any) => ({
            reference: _i?.Reference,
            loanId: _i?.LoanId,
            lvrLimit: _i?.LVRLimit?.map((_j: any) => ({
              label: _j?.Label,
              description: _j?.Description,
              maxLVR: _j?.MaxLVR,
            })),
            value: _i?.Value,
            requiredMaxLVR: _i?.RequiredMaxLVR,
            status: _i?.Status,
          })),
          LEM: _d?.Result?.LEM?.map((_i: any) => ({
            label: _i?.Label,
            loanId: _i?.LoanId,
            assessed: _i?.Assessed,
            margin: _i?.Margin,
            amount: _i?.Amount,
          })),
          DTI: {
            totalIncome: _d?.Result?.DTI?.TotalIncome,
            totalDebt: _d?.Result?.DTI?.TotalDebt,
            value: _d?.Result?.DTI?.Value,
            requiredMaxDTI: _d?.Result?.DTI?.RequiredMaxDTI,
            status: _d?.Result?.DTI?.Status,
          },
          deposit: {
            amount: _d?.Result?.Deposit?.Amount,
          },
        },
        warnings: _d?.Warnings?.map((_i: any) => ({
          message: _i?.Message,
          reference: _i?.Reference,
        })),
        errors: _d?.Errors?.map((_i: any) => ({
          message: _i?.Message,
          reference: _i?.Reference,
          referenceObjectType: _i?.ReferenceObjectType,
          referenceObjectId: _i?.ReferenceObjectId,
        })),
        resources: _d?.Resources?.map((_i: any) => ({
          type: _i?.Type,
          reference: _i?.Reference,
          url: _i?.URL,
        })),
        accuracy: _d?.Accuracy,
        version: {
          versionNumber: _d?.Version?.VersionNumber,
          releaseDate: _d?.Version?.ReleaseDate,
          versionReleaseNote: _d?.Version?.VersionReleaseNote,
        },
      })),
    })),
    accuracy: data?.Accuracy,
  };
};

export const generateLoanAmountApproval = (data: IResReport) => {
  const loanAmountApproval = data?.report?.cases?.reduce<any>(
    (acc: any, _case: any) => {
      const { Pass, Fail } = _case?.lenderResult.reduce(
        (a: any, current: any) => {
          const { result, errors } = current;
          if (!result) {
            a.Fail.push(current);
            return a;
          }
          const { UMI, LVR, DTI, servicing } = result;
          const isLVRFail = LVR?.reduce(
            (isFail: any, lvrItem: any) => isFail || lvrItem.status === "Fail",
            false,
          );
          if (
            (!servicing || servicing.type === "N/A"
              ? UMI.status === "Fail"
              : servicing.status === "Fail") ||
            isLVRFail ||
            DTI.status === "Fail" ||
            errors?.some((error: any) => error.reference === "Top")
          ) {
            a.Fail.push(current);
            return a;
          }
          a.Pass.push(current);
          return a;
        },
        {
          Pass: [],
          Fail: [],
        },
      );
      acc.push({
        Id: _case?.id,
        LoanAmount: _case?.loanAmount,
        Pass,
        Fail: Fail.sort((a: any) => {
          if (!a.errors?.some((error: any) => error.reference === "Top"))
            return -1;
          return 0;
        }),
      });
      return acc;
    },
    [],
  );

  return loanAmountApproval;
};

export const detailsBuilderUI = (data: any) => {
  return [
    {
      title: "Application Name",
      value: data.applicant[0]?.name ?? "",
    },
    {
      title: "Spouse / Partner",
      value: data.applicant[1]?.name ?? "",
    },
    {
      title: "No. of Dependents",
      value: data.numberOfDependents,
    },
    {
      title: "No. of Owned Vehicles",
      value: data.numberOfVehicles,
    },
  ];
};

export const getTermsByMos = (monthCount: any) => {
  if (!monthCount) return "0 yrs";
  const month = monthCount % 12;
  const year = Math.floor(monthCount / 12);
  return (year ? `${year} yrs` : "") + (month ? ` ${month} mos` : "");
};

export const loansFilterBySecurity = (
  loans: IDetailsLoan[],
  security: IDetailsSecurity,
  loanSecurityMapping: IDetailsLoanSecurityMappings[],
) => {
  if (!security) return [];

  if (loanSecurityMapping?.length === 0) {
    return loans;
  }

  const _list = (loans || []).filter((_loan) => {
    return (
      !!(loanSecurityMapping || []).find(
        (_i) =>
          _i.loanId === _loan.loanId && _i.securityId === security?.securityId,
      )?.loanId || false
    );
  });

  return _list;
};

export const securityBuilderUI = (data: any) => {
  return [
    {
      title: "Security Type",
      value: data.securityType,
    },
    {
      title: "Value",
      value: currency(data.value, {
        precision: 0,
      }).format(),
    },
    {
      title: "Usage",
      value: usageTypeData.find((_i: any) => _i.code === data.usageType)?.name,
    },
    {
      title: "Property Type",
      value: data.propertyType?.reduce((acc: any, str: string) => {
        const type =
          propertyTypeOptionsData.find((_i: any) => _i.code === str)?.type ||
          "";
        acc = Boolean(type) ? (Boolean(acc) ? `${acc} / ${type}` : type) : acc;
        return acc;
      }, ""),
    },
    {
      title: "RBNZ Exempt",
      value: Boolean(data.isRBNZExempt) ? "Yes" : "No",
    },
  ];
};

export const loanBuilderUI = (data: any) => {
  if (data?.loanType === "Existing" && data?.lendingAction === "None") {
    return [
      {
        title: "Loan Type",
        value: data.loanType,
      },
      {
        title: "Lending Action",
        value: "None",
      },
      {
        title: "Loan Amount",
        value: currency(data.loanAmount, {
          precision: 0,
        }).format(),
      },
    ];
  }
  if (data?.loanType === "Existing" && data?.lendingAction === "TopUp") {
    const TotalNewLoanAmount =
      (data?.liability?.limit || 0) +
      (!isNaN(data?.topUpAmount) ? data?.topUpAmount : 0);

    return [
      {
        title: "Loan Type",
        value: data.loanType,
      },
      {
        title: "Lending Action",
        value: "Top-up",
      },
      {
        title: "Original Loan Amount",
        value: currency(data.loanAmount, {
          precision: 0,
        }).format(),
      },
      {
        title: "Proposed Increase",
        value: currency(data.topUpAmount, {
          precision: 0,
        }).format(),
      },
      {
        title: "New Loan Total",
        value: currency(TotalNewLoanAmount, {
          precision: 0,
        }).format(),
      },
      {
        title: "New Loan Term",
        value: getTermsByMos(data.totalTermMonths),
      },
      {
        title: "New IO Term",
        value: getTermsByMos(data.interestOnlyTermMonths),
      },
    ];
  }
  if (data?.loanType === "Existing" && data?.lendingAction === "Refinance") {
    return [
      {
        title: "Loan Type",
        value: data.loanType,
      },
      {
        title: "Lending Action",
        value: "Refinance",
      },
      {
        title: "Loan Amount",
        value: currency(data.loanAmount, {
          precision: 0,
        }).format(),
      },
      {
        title: "New Loan Term",
        value: getTermsByMos(data.totalTermMonths),
      },
      {
        title: "New IO Term",
        value: getTermsByMos(data.interestOnlyTermMonths),
      },
    ];
  }
  return [
    {
      title: "Loan Type",
      value: data.loanType,
    },
    {
      title: "Loan Amount",
      value: currency(data.loanAmount, {
        precision: 0,
      }).format(),
    },
    {
      title: "Loan Term",
      value: getTermsByMos(data.totalTermMonths),
    },
    {
      title: "Interest Only Term",
      value: getTermsByMos(data.interestOnlyTermMonths),
    },
  ];
};

export const rentalIncomeBuilderUI = (data: any, applicants: Array<any>) => {
  const propertyBuildDate = data.attributes?.find(
    (_i: any) => _i?.name === "PropertyBuildDate",
  )?.value;
  const salePurchaseDate = data.attributes?.find(
    (_i: any) => _i?.name === "SalePurchaseDate",
  )?.value;
  const isTaxExempt = data.attributes?.find(
    (_i: any) => _i?.name === "IsTaxExempt",
  )?.value;
  const details = [
    {
      title: "Applicant",
      value:
        applicants?.find((_i: any) => _i.applicantId === data.applicantId)
          ?.name || "",
    },
    {
      title: "Amount",
      value: `${currency(data.amount, {
        precision: 2,
      }).format()} ${data.frequency}`,
    },
    {
      title: "Property Country",
      value:
        data.attributes?.find((_i: any) => _i?.name === "PropertyCountry")
          ?.value || "",
    },
    {
      title: "Property Build Date",
      value: DateFns.isValid(new Date(propertyBuildDate ?? ""))
        ? DateFns.format(new Date(propertyBuildDate), "d MMM yyyy")
        : "",
    },
    {
      title: "Sale & Purchase Date",
      value: DateFns.isValid(new Date(salePurchaseDate ?? ""))
        ? DateFns.format(new Date(salePurchaseDate), "d MMM yyyy")
        : "",
    },
    {
      title: "Tax Exempt",
      value:
        isTaxExempt !== undefined
          ? isTaxExempt === true
            ? "Yes"
            : "No"
          : "N/A",
    },
  ];

  return details;
};

export const borderIncomeBuilderUI = (data: any, applicants: Array<any>) => {
  const type =
    data.attributes?.find((_i: any) => _i?.name === "BoarderIncomeType")
      ?.value || "";
  return [
    {
      title: "Applicant",
      value:
        applicants?.find((_i: any) => _i.applicantId === data.applicantId)
          ?.name || "",
    },
    {
      title: "Amount",
      value: `${currency(data.amount, {
        precision: 2,
      }).format()} ${data.frequency}`,
    },
    {
      title: "Boarder Type",
      value: boarderType.find((_i: any) => _i.value === type)?.label,
    },
  ];
};

export const liabilityBuilderUI = (data: any) => {
  const type = LiabilityTypeLiabilitiesData.find(
    (_i: any) => _i["LiabilityType.Code"] === data.liabilityType,
  ) ?? { "LiabilityType.Name": "" };

  const details = [
    {
      title: "Liability Type",
      value: type["LiabilityType.Name"] ?? "",
    },
    {
      title: "Repayments",
      value: data.repaymentAmount
        ? `${currency(data.repaymentAmount, {
            precision: 0,
          }).format()} ${data.repaymentFrequency}`
        : "N/A",
    },
  ];

  if (data.limit) {
    details.push({
      title: "Limit",
      value: data.limit
        ? `${currency(data.limit, {
            precision: 2,
          }).format()}`
        : "",
    });
  }
  details.push({
    title: "Balance",
    value: data.balance
      ? `${currency(data.balance, {
          precision: 2,
        }).format()}`
      : "",
  });

  if (data.lender) {
    details.push({
      title: "Lender",
      value: data.lender,
    });
  }

  if (data.interestRate !== null) {
    details.push({
      title: "Interest Rate",
      value: `${
        typeof data.interestRate === "number"
          ? decimalToPercentage(data.interestRate)
          : data.interestRate
      } annually`,
    });
  }

  if (data.startDate !== null) {
    details.push({
      title: "Start Date",
      value: DateFns.isValid(new Date(data?.startDate ?? ""))
        ? DateFns.format(new Date(data.startDate), "d MMM yyyy")
        : "",
    });
  }

  if (data.totalTermMonths !== null) {
    details.push({
      title: "Loan Term",
      value: data.totalTermMonths ? `${data.totalTermMonths / 12} years` : "0",
    });
  }

  if (data.isRevolvingCredit !== null) {
    details.push({
      title: "Revolving Credit / Redraw Facility",
      value: Boolean(data.isRevolvingCredit) ? "Yes" : "No",
    });
  }

  return details;
};

export const salaryIncomeBuilderUI = (
  list: Array<any>,
  applicants: Array<any>,
) => {
  return list?.reduce((acc, data) => {
    const type = incomeTypeSalaryWagesData.find(
      (_i: any) => _i["IncomeType.Code"] === data.incomeType,
    ) ?? { "IncomeType.Name": "" };
    const kiwisaver = data.attributes?.find(
      (_i: any) => _i?.name === "Kiwisaver",
    )?.value;
    return [
      ...acc,
      {
        applicant: applicants?.find(
          (_i: any) => _i.applicantId === data.applicantId,
        )?.name,
        type: type["IncomeType.Name"] ?? "",
        amount: `${currency(data.amount, {
          precision: 0,
        }).format()} ${data.frequency}`,
        kiwisaver:
          kiwisaver !== undefined
            ? kiwisaver === true
              ? `Yes (${
                  (data.attributes?.find(
                    (_i: any) => _i?.name === "KiwisaverContribution",
                  )?.value ?? 0) * 100
                }%)`
              : "No"
            : "N/A",
      },
    ];
  }, []);
};

export const salaryOtherIncomeBuilderUI = (
  list: Array<any>,
  applicants: Array<any>,
) => {
  return list?.reduce((acc, data) => {
    const type = incomeTypeOtherData.find(
      (_i: any) => _i["IncomeType.Code"] === data.incomeType,
    ) ?? { "IncomeType.Name": "" };
    return [
      ...acc,
      {
        applicant: applicants?.find(
          (_i: any) => _i.applicantId === data.applicantId,
        )?.name,
        type: type["IncomeType.Name"] ?? "",
        amount: `${currency(data.amount, {
          precision: 0,
        }).format()} ${data.frequency}`,
      },
    ];
  }, []);
};

export const outgoingCCBuilderUI = (list: Array<any>) => {
  return list?.reduce((acc, data) => {
    const type = LiabilityTypeCreditCardsData.find(
      (_i: any) => _i["LiabilityType.Code"] === data.liabilityType,
    ) ?? { "LiabilityType.Name": "" };
    return [
      ...acc,
      {
        type: type["LiabilityType.Name"] ?? "",
        limit: `${currency(data.limit, {
          precision: 0,
        }).format()}`,
        lender: data.lender,
      },
    ];
  }, []);
};

export const expensesBuilderUI = (list: Array<any>, key: any) => {
  const filteredList = list.filter((_data: any) => _data.group === key);
  const newList = filteredList?.reduce((acc, data) => {
    return [
      ...acc,
      {
        type: data.label,
        amount: `${currency(data.amount, {
          precision: 2,
        }).format()} ${data.frequency}`,
      },
    ];
  }, []);
  return newList;
};

export const lenderLVRBuilderUI = (list: Array<any>) => {
  return list?.reduce((acc, data) => {
    return [
      ...acc,
      {
        type: data.label,
        description: data.description,
        maxLVR: `Max LVR: ${
          typeof data.maxLVR === "number"
            ? decimalToPercentage(data.maxLVR)
            : data.maxLVR
        }`,
      },
    ];
  }, []);
};

export const lenderDTIBuilderUI = (dti: any) => {
  return [
    {
      label: "Total Debt",
      value: `$${dti?.totalDebt}`,
    },
    {
      label: "Total Income",
      value: `$${dti?.totalIncome}`,
    },
    {
      label: "Debt to Income Ratio",
      value: dti?.value,
    },
  ];
};

export const lenderLEMBuilderUI = (lem: any) => {
  return [
    {
      label: "Assessed",
      value: `${
        typeof lem?.assessed === "number"
          ? decimalToPercentage(lem?.assessed)
          : lem?.assessed ?? "0%"
      }`,
    },
    {
      label: "Margin",
      value: lem?.margin
        ? `${
            typeof lem?.margin === "number"
              ? decimalToPercentage(lem?.margin) + " p.a"
              : lem?.margin ?? "N/A"
          }`
        : "N/A",
    },
  ];
};

export const lenderUMIBuilderUI = (list: any) => {
  return list?.reduce((acc: any, data: any) => {
    const enteredDef = data?.entered?.definition
      ? data?.entered?.definition
      : data?.entered?.frequency
      ? `p.${data?.entered?.frequency?.charAt(0)?.toLowerCase()}`
      : data?.entered?.interestRate
      ? `@ ${data?.entered?.interestRate * 100}%`
      : "";
    const assessedFrequency = data?.assessed?.frequency
      ?.charAt(0)
      ?.toLowerCase();
    return [
      ...acc,
      {
        label: data.label,
        entered: data?.entered?.amount
          ? `${currency(data?.entered?.amount, {
              precision: 2,
            }).format()} ${enteredDef}`
          : "N/A",
        assessed: `${currency(data?.assessed?.amount, {
          precision: 2,
        }).format()} p.${assessedFrequency}`,
      },
    ];
  }, []);
};

export const lenderUMICalculatorBuilderUI = (umiResult: any) => {
  return [
    {
      label: "Total Income",
      value: `${currency(umiResult?.totalMonthlyIncome, {
        precision: 2,
      }).format()} p.m`,
    },
    {
      label: "Total Income",
      value: `${currency(umiResult?.totalMonthlyExpense, {
        precision: 2,
      }).format()} p.m`,
    },
    {
      label: `Bank Calculated  ${
        umiResult?.servicing?.type === "N/A"
          ? "UMI"
          : umiResult?.servicing?.type
      }`,
      value:
        !umiResult?.servicing ||
        umiResult?.servicing?.type === "N/A" ||
        umiResult?.servicing?.type === "UMI"
          ? `${currency(umiResult?.UMI?.value || 0).format()} p.m`
          : umiResult?.servicing?.type === "NSR"
          ? `${decimalToPercentage(umiResult?.NSR?.value)}`
          : `${decimalToPercentage(umiResult?.servicing?.value)}`,
    },
  ];
};
